/**
 * Download a backend file (PDF, image, etc.) that requires JWT authentication.
 *
 * Browsers don't include Authorization headers when you use window.open() or
 * <a href> — so any /api/... endpoint protected by a Bearer token will return
 * 401 if you try the naïve approach. This helper:
 *
 *   1. Fetches with proper Authorization header
 *   2. Converts to blob
 *   3. Triggers a synthetic <a download> click
 *   4. Cleans up the blob URL
 *
 * Falls back to opening the raw URL in a new tab on failure (rare — but at
 * least the user sees the backend error rather than a blank page).
 *
 * @param {string} pathOrUrl  Either an absolute URL or a path beginning with `/api/...`.
 *                            If a path, REACT_APP_BACKEND_URL is prepended.
 * @param {string} filename   Suggested filename for the saved file.
 * @returns {Promise<{ok: boolean, status?: number, error?: string}>}
 */
export async function downloadAuthedFile(pathOrUrl, filename = 'download.pdf') {
  if (!pathOrUrl) return { ok: false, error: 'no_url' };
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${base}${pathOrUrl}`;
  const token = sessionStorage.getItem('token') || sessionStorage.getItem('access_token') || '';

  try {
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      return { ok: false, status: response.status, error: `HTTP ${response.status}` };
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      a.remove();
    }, 100);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || 'fetch_failed' };
  }
}

export default downloadAuthedFile;
